import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import type { DistributionClient } from '../lib/api';
import { getCybertubeLinkQrUrl } from '../lib/cybertube-link-qr';
import { formatDuration } from '../lib/format';
import type {
  CybertubeAccountStatus,
  CybertubeCatalogItem,
  CybertubeLinkStatus,
  CybertubePlaylist,
} from '../lib/types';
import { useI18n } from '../lib/use-i18n';

type Tab = 'home' | 'subscriptions' | 'history' | 'playlists';

const EMPTY: CybertubeCatalogItem[] = [];
const LINK_POLL_MS = 5_000;

export function CyberTubeView({ client, query, onOpen }: {
  client: DistributionClient;
  query: string;
  onOpen: (sourceId: string) => void;
}) {
  const { t } = useI18n();
  const [tab, setTab] = useState<Tab>('home');
  const [items, setItems] = useState<CybertubeCatalogItem[]>(EMPTY);
  const [playlists, setPlaylists] = useState<CybertubePlaylist[]>([]);
  const [openPlaylist, setOpenPlaylist] = useState<CybertubePlaylist | null>(null);
  const [personalized, setPersonalized] = useState(false);
  const [needsLink, setNeedsLink] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [account, setAccount] = useState<CybertubeAccountStatus | null>(null);
  const [linking, setLinking] = useState(false);
  const requestVersion = useRef(0);

  const searching = query.trim().length >= 2;

  useEffect(() => {
    let cancelled = false;
    client.cybertubeAccount()
      .then((status) => { if (!cancelled) setAccount(status); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [client]);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setLoading(true);
    setError(false);
    setNeedsLink(false);
    try {
      if (searching) {
        const found = await client.cybertubeSearch(query.trim());
        if (requestVersion.current !== version) return;
        setItems(found.items);
        setPersonalized(false);
        return;
      }
      if (tab === 'playlists' && !openPlaylist) {
        const found = await client.cybertubePlaylists();
        if (requestVersion.current !== version) return;
        setPlaylists(found.items);
        setNeedsLink(found.linked === false);
        return;
      }
      if (openPlaylist) {
        const found = await client.cybertubePlaylistItems(openPlaylist.playlistId);
        if (requestVersion.current !== version) return;
        setItems(found.items);
        setNeedsLink(found.linked === false);
        return;
      }
      const found = tab === 'subscriptions'
        ? await client.cybertubeSubscriptions()
        : tab === 'history'
          ? await client.cybertubeHistory()
          : await client.cybertubeHome();
      if (requestVersion.current !== version) return;
      setItems(found.items);
      setPersonalized(Boolean(found.personalized));
      setNeedsLink(found.linked === false);
    } catch {
      if (requestVersion.current !== version) return;
      setItems([]);
      setError(true);
    } finally {
      if (requestVersion.current === version) setLoading(false);
    }
  }, [client, openPlaylist, query, searching, tab]);

  useEffect(() => {
    // Debounce only while typing; tab switches should feel instant.
    const delay = searching ? 300 : 0;
    const timer = window.setTimeout(() => void load(), delay);
    return () => window.clearTimeout(timer);
  }, [load, searching]);

  const onLinked = useCallback((status: CybertubeAccountStatus) => {
    setAccount(status);
    setLinking(false);
    void load();
  }, [load]);

  async function unlink() {
    await client.unlinkCybertube().catch(() => undefined);
    const status = await client.cybertubeAccount().catch(() => null);
    setAccount(status);
    void load();
  }

  const showTabs = !searching;
  const empty = !loading && !items.length && !(tab === 'playlists' && playlists.length);

  return (
    <section className="cybertube-view">
      <header className="cybertube-head">
        <div className="cybertube-title">
          <span>CYBERTUBE</span>
          <h1>{searching ? t('cybertubeResults') : openPlaylist ? openPlaylist.title : t(tabTitleKey(tab))}</h1>
        </div>
        <AccountChip
          account={account}
          onConnect={() => setLinking(true)}
          onDisconnect={() => void unlink()}
        />
      </header>

      {showTabs && (
        <nav className="cybertube-tabs" aria-label="CyberTube">
          {(['home', 'subscriptions', 'history', 'playlists'] as Tab[]).map((item) => (
            <button
              key={item}
              type="button"
              data-focusable="true"
              className={`cybertube-tab ${tab === item && !openPlaylist ? 'active' : ''}`}
              onClick={() => { setOpenPlaylist(null); setTab(item); }}
            >
              {t(tabTitleKey(item))}
            </button>
          ))}
        </nav>
      )}

      {openPlaylist && (
        <button className="text-button" data-focusable="true" onClick={() => setOpenPlaylist(null)}>
          ← {t('cybertubePlaylists')}
        </button>
      )}

      {loading && <div className="loading-line" />}

      {!loading && needsLink && (
        <ConnectPrompt onConnect={() => setLinking(true)} />
      )}

      {!loading && !needsLink && !searching && tab === 'home' && !personalized && items.length > 0 && (
        <p className="cybertube-note">{t('cybertubeConnectForRecommendations')}</p>
      )}

      {tab === 'playlists' && !openPlaylist && !needsLink ? (
        <div className="cybertube-grid">
          {playlists.map((playlist) => (
            <button
              key={playlist.playlistId}
              className="cybertube-card"
              data-focusable="true"
              onClick={() => setOpenPlaylist(playlist)}
            >
              <span
                className="cybertube-art"
                style={playlist.thumbnail ? { backgroundImage: `url("${playlist.thumbnail}")` } : undefined}
              >
                <span className="cybertube-play">☰</span>
                {playlist.videoCount ? <small>{playlist.videoCount}</small> : null}
              </span>
              <span className="cybertube-copy">
                <b>{playlist.title}</b>
                <small>{playlist.author || t('cybertubePlaylists')}</small>
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {(tab !== 'playlists' || openPlaylist || searching) && items.length > 0 && (
        <VideoGrid items={items} onOpen={onOpen} />
      )}

      {empty && !needsLink && (
        <div className="empty-state">
          <b>{error ? t('temporarilyUnavailable') : t('noResults')}</b>
          <span>{error ? t('tryAgainSoon') : t('cybertubeEmptyHint')}</span>
        </div>
      )}

      {linking && (
        <LinkDialog client={client} onClose={() => setLinking(false)} onLinked={onLinked} />
      )}
    </section>
  );
}

function tabTitleKey(tab: Tab) {
  return tab === 'home'
    ? 'cybertubeHome' as const
    : tab === 'subscriptions'
      ? 'cybertubeSubscriptions' as const
      : tab === 'history'
        ? 'cybertubeHistory' as const
        : 'cybertubePlaylists' as const;
}

function VideoGrid({ items, onOpen }: {
  items: CybertubeCatalogItem[];
  onOpen: (sourceId: string) => void;
}) {
  const { locale, t } = useI18n();
  return (
    <div className="cybertube-grid">
      {items.map((item) => (
        <button
          key={item.sourceId}
          className="cybertube-card"
          data-focusable="true"
          onClick={() => onOpen(item.sourceId)}
        >
          <span
            className="cybertube-art"
            style={item.thumbnail ? { backgroundImage: `url("${item.thumbnail}")` } : undefined}
          >
            <span className="cybertube-play">▶</span>
            {item.durationSec ? <small>{formatDuration(item.durationSec, locale)}</small> : null}
            {item.ready ? <em title={t('cybertubeOffline')}>●</em> : null}
            {['queued', 'downloading', 'processing'].includes(item.state)
              ? <i>{Math.round(item.progress)}%</i>
              : null}
          </span>
          <span className="cybertube-copy">
            <b>{item.title}</b>
            <small>{item.channelTitle || t('cybertube')}</small>
            {(item.viewCountText || item.publishedText) && (
              <u>{[item.viewCountText, item.publishedText].filter(Boolean).join(' · ')}</u>
            )}
          </span>
        </button>
      ))}
    </div>
  );
}

function AccountChip({ account, onConnect, onDisconnect }: {
  account: CybertubeAccountStatus | null;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const { t } = useI18n();
  if (!account?.linked) {
    return (
      <button className="cybertube-connect" data-focusable="true" onClick={onConnect}>
        {t('cybertubeConnect')}
      </button>
    );
  }
  return (
    <div className="cybertube-account">
      {account.account?.channelAvatar
        ? <img src={account.account.channelAvatar} alt="" />
        : <span className="cybertube-account-mark">▶</span>}
      <div>
        <b>{account.account?.channelName || t('cybertubeConnected')}</b>
        <small>{t('cybertubeConnected')}</small>
      </div>
      <button className="text-button" data-focusable="true" onClick={onDisconnect}>
        {t('cybertubeDisconnect')}
      </button>
    </div>
  );
}

function ConnectPrompt({ onConnect }: { onConnect: () => void }) {
  const { t } = useI18n();
  return (
    <div className="empty-state">
      <b>{t('cybertubeConnectTitle')}</b>
      <span>{t('cybertubeConnectHint')}</span>
      <button className="primary-action" data-focusable="true" onClick={onConnect}>
        {t('cybertubeConnect')}
      </button>
    </div>
  );
}

/**
 * TV device flow: show a QR with the YouTube activate URL (code prefilled).
 * Phone scan opens the consent page; we poll until Google approves.
 */
function LinkDialog({ client, onClose, onLinked }: {
  client: DistributionClient;
  onClose: () => void;
  onLinked: (status: CybertubeAccountStatus) => void;
}) {
  const { t } = useI18n();
  const [status, setStatus] = useState<CybertubeLinkStatus | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    let timer = 0;

    const poll = async () => {
      try {
        const next = await client.cybertubeLinkStatus();
        if (!alive.current) return;
        setStatus(next);
        if (next.status === 'linked') {
          const account = await client.cybertubeAccount();
          if (alive.current) onLinked(account);
          return;
        }
        if (next.status === 'error' || next.status === 'expired') {
          setFailed(true);
          return;
        }
      } catch {
        if (!alive.current) return;
        setFailed(true);
        return;
      }
      timer = window.setTimeout(() => void poll(), LINK_POLL_MS);
    };

    client.startCybertubeLink()
      .then((start) => {
        if (!alive.current) return;
        setStatus({ status: 'pending', account: null, ...start });
        timer = window.setTimeout(() => void poll(), LINK_POLL_MS);
      })
      .catch(() => { if (alive.current) setFailed(true); });

    return () => { alive.current = false; window.clearTimeout(timer); };
  }, [client, onLinked]);

  useEffect(() => {
    const url = getCybertubeLinkQrUrl(status);
    if (!url) {
      setQrDataUrl('');
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(url, { width: 280, margin: 2, errorCorrectionLevel: 'M' })
      .then((dataUrl) => {
        if (!cancelled) setQrDataUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl('');
      });
    return () => { cancelled = true; };
  }, [status?.userCode, status?.verificationUrl]);

  return (
    <div className="cybertube-link" role="dialog" aria-modal="true" aria-label={t('cybertubeConnect')}>
      <div className="cybertube-link-card">
        <button className="release-launch-close" data-focusable="true" onClick={onClose} aria-label={t('close')}>×</button>
        <small>CYBERTUBE</small>
        <h2>{t('cybertubeConnectTitle')}</h2>
        {failed ? (
          <p className="cybertube-link-error">{t('cybertubeLinkFailed')}</p>
        ) : status?.userCode ? (
          <>
            <p>{t('cybertubeLinkStep1')}</p>
            {qrDataUrl ? (
              <img
                className="cybertube-link-qr"
                src={qrDataUrl}
                alt={t('cybertubeLinkStep1')}
                width={280}
                height={280}
              />
            ) : (
              <div className="cybertube-link-qr-skel" aria-hidden />
            )}
            <p>{t('cybertubeLinkStep2')}</p>
            <strong className="cybertube-link-code">{status.userCode}</strong>
            <p className="cybertube-link-wait">{t('cybertubeLinkWaiting')}</p>
          </>
        ) : (
          <p>{t('pleaseWait')}</p>
        )}
      </div>
    </div>
  );
}
